import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { gsap } from 'gsap';
import { 
  BookOpen, Calculator, Sparkles, Download, 
  RefreshCw, ArrowRight, CheckCircle, Star,
  Lightbulb, Clock, Users
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import { 
  GSAPTextReveal, 
  ScrollScaleReveal,
  StaggeredCardsContainer,
  MaskReveal,
  MagneticButton,
  ParallaxElement,
  NeonGlowLine
} from '@/components/ui/GSAPAnimations';

const tools = [
  {
    id: 'lesson-planner',
    name: 'AI Lesson Planner',
    description: 'Generate structured lesson plans in seconds with AI assistance.',
    icon: BookOpen,
    color: 'teal',
  },
  {
    id: 'grade-normalizer',
    name: 'Grade Normalizer',
    description: 'Adjust and curve grades with statistical precision.',
    icon: Calculator,
    color: 'orange',
  },
  {
    id: 'icebreaker',
    name: 'Icebreaker Generator',
    description: 'Fun conversation starters for your classroom.',
    icon: Sparkles,
    color: 'teal',
  },
];

const icebreakers = [
  "If you could have dinner with any historical figure, who would it be and why?",
  "What's a skill you'd love to learn in the next year?",
  "If you could instantly become an expert in something, what would it be?",
  "What's the most interesting thing you learned this week?",
  "If you could travel to any time period, when would you go?",
  "What's a book, movie, or show that changed your perspective?",
  "If you could have any superpower for just one day, what would you choose?",
  "What's the best piece of advice you've ever received?",
  "If you could visit any place in the world, where would you go?",
  "What's something you're proud of that you rarely get to talk about?",
];

const lessonPlanTemplate = {
  title: '',
  grade: 'Class 8',
  duration: '45 minutes',
  objectives: [
    'Students will be able to define and identify the process',
    'Students will understand the key components involved',
    'Students will apply knowledge through hands-on activities',
  ],
  materials: [
    'Whiteboard and markers',
    'Printed worksheets',
    'Visual aids/diagrams',
    'Student notebooks',
  ],
  introduction: '5 minutes - Begin with a hook question to engage students and activate prior knowledge.',
  instruction: '15 minutes - Present core concepts using visual aids and real-world examples.',
  practice: '15 minutes - Guided practice with worksheets. Walk around to assist struggling students.',
  assessment: '10 minutes - Quick quiz or exit ticket to assess understanding.',
  homework: 'Read Chapter 3 and complete practice problems 1-10.',
};

const benefits = [
  { icon: Clock, title: 'Save 10+ Hours/Week', desc: 'Automate repetitive tasks' },
  { icon: Lightbulb, title: 'AI-Powered Insights', desc: 'Smart suggestions for your content' },
  { icon: Users, title: 'Join 50K+ Teachers', desc: 'Growing community of educators' },
];

const TeachersPage = () => {
  const [activeTool, setActiveTool] = useState('lesson-planner');
  const [lessonTopic, setLessonTopic] = useState('');
  const [generatedLesson, setGeneratedLesson] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  
  const [highScore, setHighScore] = useState([95]);
  const [lowScore, setLowScore] = useState([45]);
  const [normalizedScores, setNormalizedScores] = useState(null);
  
  const [currentIcebreaker, setCurrentIcebreaker] = useState(null);
  const [isSpinning, setIsSpinning] = useState(false);
  
  const icebreakerRef = useRef(null);

  // Lesson Planner Handler
  const generateLessonPlan = async () => {
    if (!lessonTopic.trim()) return;
    
    setIsGenerating(true);
    
    // Animate generation
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setGeneratedLesson({
      ...lessonPlanTemplate,
      title: lessonTopic,
      objectives: [
        `Students will be able to define and explain ${lessonTopic}`,
        `Students will identify key components and processes of ${lessonTopic}`,
        `Students will apply knowledge of ${lessonTopic} through hands-on activities`,
      ],
      introduction: `5 minutes - Begin by asking: "What do you already know about ${lessonTopic}?" Write responses on the board.`,
      instruction: `15 minutes - Present the core concepts of ${lessonTopic} using visual aids, diagrams, and real-world examples.`,
    });
    setIsGenerating(false);
  };

  // Grade Normalizer Handler
  const calculateNormalized = () => {
    const high = highScore[0];
    const low = lowScore[0];
    const range = high - low;
    
    const sampleScores = [45, 52, 61, 68, 72, 78, 83, 89, 95];
    const normalized = sampleScores.map(score => {
      const normalizedScore = ((score - low) / range) * 50 + 50;
      return {
        original: score,
        normalized: Math.min(100, Math.max(0, Math.round(normalizedScore))),
      };
    });
    
    setNormalizedScores(normalized);
  };

  // Icebreaker Handler with GSAP Animation
  const spinIcebreaker = () => {
    setIsSpinning(true);
    
    const finalIndex = Math.floor(Math.random() * icebreakers.length);
    let currentIndex = 0;
    
    // Fast scramble through icebreakers
    const interval = setInterval(() => {
      currentIndex = (currentIndex + 1) % icebreakers.length;
      setCurrentIcebreaker(icebreakers[currentIndex]);
    }, 80);

    // Stop after animation
    setTimeout(() => {
      clearInterval(interval);
      setCurrentIcebreaker(icebreakers[finalIndex]);
      setIsSpinning(false);
      
      // Bounce animation
      if (icebreakerRef.current) {
        gsap.fromTo(icebreakerRef.current, 
          { scale: 0.8, opacity: 0 },
          { scale: 1, opacity: 1, duration: 0.5, ease: 'elastic.out(1, 0.5)' }
        );
      }
    }, 1500);
  };

  return (
    <div className="pt-20 min-h-screen bg-void">
      {/* ============================================
          HERO SECTION
          ============================================ */}
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 bg-mesh-gradient" />
        
        {/* Decorative Elements */}
        <ParallaxElement speed={-0.2} className="absolute top-40 right-10 opacity-20">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 30, repeat: Infinity, ease: 'linear' }}
            className="w-64 h-64 border border-teal/30 rounded-full"
          />
        </ParallaxElement>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <MaskReveal direction="up">
            <span className="font-mono text-xs text-teal tracking-widest uppercase mb-4 block">
              Free Teacher Resources
            </span>
          </MaskReveal>
          
          <GSAPTextReveal 
            text="Empower Your Classroom."
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-foreground mb-2"
            splitBy="words"
            staggerDelay={0.1}
          />
          <GSAPTextReveal 
            text="For Free."
            className="text-display text-4xl sm:text-5xl lg:text-6xl text-gradient-teal mb-6"
            splitBy="words"
            staggerDelay={0.1}
            delay={0.5}
          />
          
          <MaskReveal direction="up">
            <p className="text-muted-foreground text-lg max-w-2xl mx-auto mb-8">
              Powerful tools designed by educators, for educators. 
              Save time, engage students, and focus on what matters most—teaching.
            </p>
          </MaskReveal>

          {/* Benefits */}
          <StaggeredCardsContainer className="flex flex-wrap justify-center gap-6 mb-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={benefit.title}
                className="flex items-center gap-3 px-5 py-3 glass-card"
                whileHover={{ scale: 1.05, y: -3 }}
              >
                <benefit.icon className="w-5 h-5 text-teal" />
                <div className="text-left">
                  <p className="text-sm font-semibold text-foreground">{benefit.title}</p>
                  <p className="text-xs text-muted-foreground">{benefit.desc}</p>
                </div>
              </motion.div>
            ))}
          </StaggeredCardsContainer>
        </div>
      </section>

      {/* ============================================
          TOOLS SECTION
          ============================================ */}
      <section className="py-24 relative">
        {/* Neon Line */}
        <div className="absolute top-0 left-0 right-0">
          <NeonGlowLine />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Tool Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {tools.map((tool, index) => (
              <MagneticButton key={tool.id} strength={15}>
                <motion.button
                  onClick={() => setActiveTool(tool.id)}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className={cn(
                    'flex items-center gap-3 px-6 py-4 rounded-xl border transition-all duration-300',
                    activeTool === tool.id
                      ? tool.color === 'teal'
                        ? 'bg-teal/10 border-teal/30 text-teal shadow-teal-glow'
                        : 'bg-orange/10 border-orange/30 text-orange shadow-orange-glow'
                      : 'bg-glass-surface border-glass-border text-muted-foreground hover:text-foreground hover:border-muted-foreground'
                  )}
                >
                  <tool.icon className="w-5 h-5" />
                  <span className="font-display font-semibold">{tool.name}</span>
                </motion.button>
              </MagneticButton>
            ))}
          </div>

          {/* Tool Content */}
          <AnimatePresence mode="wait">
            {/* ==================
                LESSON PLANNER
                ================== */}
            {activeTool === 'lesson-planner' && (
              <motion.div
                key="lesson-planner"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-4xl mx-auto"
              >
                <ScrollScaleReveal>
                  <div className="glass-card p-8">
                    <h2 className="text-display text-2xl text-foreground mb-2">
                      AI Lesson Planner
                    </h2>
                    <p className="text-muted-foreground mb-6">
                      Enter your topic and let AI generate a structured lesson plan.
                    </p>

                    <div className="flex flex-col sm:flex-row gap-4 mb-8">
                      <Input
                        value={lessonTopic}
                        onChange={(e) => setLessonTopic(e.target.value)}
                        placeholder="Enter topic (e.g., Photosynthesis)"
                        className="flex-1 bg-void-light border-glass-border text-foreground placeholder:text-muted-foreground focus:border-teal"
                        onKeyDown={(e) => e.key === 'Enter' && generateLessonPlan()}
                      />
                      <MagneticButton strength={20}>
                        <motion.button
                          onClick={generateLessonPlan}
                          disabled={isGenerating || !lessonTopic.trim()}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className="btn-primary disabled:opacity-50"
                        >
                          {isGenerating ? (
                            <RefreshCw className="w-5 h-5 animate-spin" />
                          ) : (
                            <>
                              Generate
                              <ArrowRight className="w-4 h-4" />
                            </>
                          )}
                        </motion.button>
                      </MagneticButton>
                    </div>

                    {/* Generated Lesson */}
                    <AnimatePresence>
                      {generatedLesson && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                          className="border-t border-glass-border pt-8"
                        >
                          <div className="flex items-center justify-between mb-6">
                            <h3 className="text-display text-xl text-teal">
                              {generatedLesson.title}
                            </h3>
                            <span className="font-mono text-xs text-muted-foreground">
                              {generatedLesson.grade} • {generatedLesson.duration}
                            </span>
                          </div>

                          <StaggeredCardsContainer className="space-y-6">
                            <div>
                              <h4 className="text-sm font-semibold text-foreground mb-2">
                                Learning Objectives
                              </h4>
                              <ul className="space-y-2">
                                {generatedLesson.objectives.map((obj, i) => (
                                  <motion.li 
                                    key={i} 
                                    className="flex items-start gap-2 text-sm text-muted-foreground"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.1 }}
                                  >
                                    <CheckCircle className="w-4 h-4 text-teal mt-0.5 flex-shrink-0" />
                                    {obj}
                                  </motion.li>
                                ))}
                              </ul>
                            </div>

                            <div className="grid md:grid-cols-2 gap-6">
                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">
                                  Materials Needed
                                </h4>
                                <ul className="space-y-1">
                                  {generatedLesson.materials.map((mat, i) => (
                                    <li key={i} className="text-sm text-muted-foreground">
                                      • {mat}
                                    </li>
                                  ))}
                                </ul>
                              </div>

                              <div>
                                <h4 className="text-sm font-semibold text-foreground mb-2">
                                  Lesson Structure
                                </h4>
                                <div className="space-y-2 text-sm text-muted-foreground">
                                  <p><span className="text-teal">Introduction:</span> {generatedLesson.introduction}</p>
                                  <p><span className="text-teal">Instruction:</span> {generatedLesson.instruction}</p>
                                  <p><span className="text-teal">Practice:</span> {generatedLesson.practice}</p>
                                  <p><span className="text-teal">Assessment:</span> {generatedLesson.assessment}</p>
                                </div>
                              </div>
                            </div>

                            <div className="flex justify-end">
                              <MagneticButton strength={15}>
                                <motion.button
                                  whileHover={{ scale: 1.02 }}
                                  className="btn-secondary"
                                >
                                  <Download className="w-4 h-4" />
                                  Download PDF
                                </motion.button>
                              </MagneticButton>
                            </div>
                          </StaggeredCardsContainer>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </ScrollScaleReveal>
              </motion.div>
            )}

            {/* ==================
                GRADE NORMALIZER
                ================== */}
            {activeTool === 'grade-normalizer' && (
              <motion.div
                key="grade-normalizer"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-4xl mx-auto"
              >
                <ScrollScaleReveal>
                  <div className="glass-card p-8">
                    <h2 className="text-display text-2xl text-foreground mb-2">
                      Grade Normalizer
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Adjust grade distributions with statistical curve normalization.
                    </p>

                    <div className="grid md:grid-cols-2 gap-8 mb-8">
                      <div>
                        <label className="text-sm font-medium text-foreground mb-4 block">
                          Highest Score: <span className="text-teal font-mono">{highScore[0]}</span>
                        </label>
                        <Slider
                          value={highScore}
                          onValueChange={setHighScore}
                          max={100}
                          min={0}
                          step={1}
                          className="mb-6"
                        />

                        <label className="text-sm font-medium text-foreground mb-4 block">
                          Lowest Score: <span className="text-orange font-mono">{lowScore[0]}</span>
                        </label>
                        <Slider
                          value={lowScore}
                          onValueChange={setLowScore}
                          max={100}
                          min={0}
                          step={1}
                        />
                      </div>

                      <div className="flex items-center justify-center">
                        <MagneticButton strength={25}>
                          <motion.button
                            onClick={calculateNormalized}
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            className="btn-primary px-8 py-4"
                          >
                            <Calculator className="w-5 h-5" />
                            Calculate Curve
                          </motion.button>
                        </MagneticButton>
                      </div>
                    </div>

                    {/* Results */}
                    <AnimatePresence>
                      {normalizedScores && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="border-t border-glass-border pt-8"
                        >
                          <h4 className="text-sm font-semibold text-foreground mb-4">
                            Normalized Results
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full">
                              <thead>
                                <tr className="text-left text-sm text-muted-foreground border-b border-glass-border">
                                  <th className="pb-3">Original Score</th>
                                  <th className="pb-3">Normalized Score</th>
                                  <th className="pb-3">Change</th>
                                </tr>
                              </thead>
                              <tbody>
                                {normalizedScores.map((score, i) => (
                                  <motion.tr 
                                    key={i} 
                                    className="border-b border-glass-border/50"
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: i * 0.05 }}
                                  >
                                    <td className="py-3 font-mono text-muted-foreground">{score.original}</td>
                                    <td className="py-3 text-teal font-mono font-semibold">{score.normalized}</td>
                                    <td className={cn(
                                      'py-3 font-mono',
                                      score.normalized > score.original ? 'text-green-500' : 'text-orange'
                                    )}>
                                      {score.normalized > score.original ? '+' : ''}
                                      {score.normalized - score.original}
                                    </td>
                                  </motion.tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </ScrollScaleReveal>
              </motion.div>
            )}

            {/* ==================
                ICEBREAKER GENERATOR
                ================== */}
            {activeTool === 'icebreaker' && (
              <motion.div
                key="icebreaker"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                className="max-w-2xl mx-auto"
              >
                <ScrollScaleReveal>
                  <div className="glass-card p-8 text-center">
                    <h2 className="text-display text-2xl text-foreground mb-2">
                      Icebreaker Generator
                    </h2>
                    <p className="text-muted-foreground mb-8">
                      Fun conversation starters to energize your classroom.
                    </p>

                    {/* Result Display */}
                    <div className="min-h-[150px] flex items-center justify-center mb-8 relative">
                      {/* Decorative Circle */}
                      <motion.div 
                        className="absolute inset-0 flex items-center justify-center pointer-events-none"
                        animate={isSpinning ? { rotate: 360 } : { rotate: 0 }}
                        transition={{ duration: 0.5, repeat: isSpinning ? Infinity : 0, ease: 'linear' }}
                      >
                        <div className="w-48 h-48 border-2 border-dashed border-teal/20 rounded-full" />
                      </motion.div>

                      <AnimatePresence mode="wait">
                        {currentIcebreaker ? (
                          <motion.p
                            ref={icebreakerRef}
                            key={currentIcebreaker}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            exit={{ opacity: 0, scale: 0.8 }}
                            className={cn(
                              'text-xl text-foreground font-display leading-relaxed px-4 relative z-10',
                              isSpinning && 'blur-sm'
                            )}
                          >
                            &quot;{currentIcebreaker}&quot;
                          </motion.p>
                        ) : (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="text-muted-foreground"
                          >
                            Click the button to generate an icebreaker!
                          </motion.p>
                        )}
                      </AnimatePresence>
                    </div>

                    {/* Spin Button */}
                    <MagneticButton strength={30}>
                      <motion.button
                        onClick={spinIcebreaker}
                        disabled={isSpinning}
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        className="btn-primary text-lg px-10 py-4 disabled:opacity-50"
                      >
                        {isSpinning ? (
                          <RefreshCw className="w-6 h-6 animate-spin" />
                        ) : (
                          <>
                            <Sparkles className="w-6 h-6" />
                            Spin!
                          </>
                        )}
                      </motion.button>
                    </MagneticButton>
                  </div>
                </ScrollScaleReveal>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>

      {/* ============================================
          CTA SECTION
          ============================================ */}
      <section className="py-24 bg-void-light/30 relative overflow-hidden">
        <ParallaxElement speed={0.1} className="absolute inset-0 pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal/5 rounded-full blur-3xl" />
        </ParallaxElement>

        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <ScrollScaleReveal>
            <motion.div
              animate={{ rotate: [0, 5, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            >
              <Star className="w-12 h-12 text-orange mx-auto mb-6" />
            </motion.div>
            
            <h2 className="text-display text-3xl sm:text-4xl text-foreground mb-4">
              Ready for More?
            </h2>
            <p className="text-muted-foreground text-lg mb-8 max-w-xl mx-auto">
              Get full access to certified courses, networking opportunities, 
              and career growth tools with the Lynkt platform.
            </p>
            
            <MagneticButton strength={25}>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="btn-primary text-lg px-8 py-4"
              >
                <Download className="w-5 h-5" />
                Download Lynkt App
              </motion.button>
            </MagneticButton>
          </ScrollScaleReveal>
        </div>
      </section>
    </div>
  );
};

export default TeachersPage;
